// ============================================================================
// VINScanner.jsx - VIN OCR Scanner (Using Tesseract.js)
// ============================================================================
// Takes a photo of the VIN (from windshield, door jamb, engine, etc.)
// Uses OCR to extract the 17-character VIN
// More reliable than barcode scanning
// ============================================================================

import React, { Component } from 'react';
import Tesseract from 'tesseract.js';

class VINScanner extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showCamera: true,
      photo: null,
      detectedVIN: null,
      ocrProcessing: false,
      error: null,
      ocrProgress: 0,
      // Real-time scanning
      realtimeVIN: null,
      scanning: false,
      stream: null
    };

    this.videoRef = React.createRef();
    this.canvasRef = React.createRef();
    this.scanInterval = null;
  }

  componentDidMount() {
    if (this.state.showCamera) {
      this.startCamera();
    }
  }

  componentWillUnmount() {
    if (this.scanInterval) {
      clearInterval(this.scanInterval);
    }
    this.stopCamera();
  }

  startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      if (this.videoRef.current) {
        this.videoRef.current.srcObject = stream;
        this.videoRef.current.play();
      }

      this.setState({ stream });
      console.log("✅ Camera started");

    } catch (err) {
      console.error('❌ Camera access error:', err);
      this.setState({
        error: 'Camera access denied. Please enable camera permissions.'
      });
    }
  };

  stopCamera = () => {
    if (this.state.stream) {
      this.state.stream.getTracks().forEach(track => track.stop());
    }
  };

  handlePhotoCapture = async (photoData) => {
    console.log("📸 VIN photo captured");

    this.setState({
      photo: photoData,
      showCamera: false,
      ocrProcessing: true,
      ocrProgress: 0
    });

    // Perform OCR on the VIN image
    try {
      console.log("🔍 Starting VIN OCR processing...");

      const { data: { text } } = await Tesseract.recognize(
        photoData.preview,
        'eng',
        {
          logger: (m) => {
            console.log(m);
            if (m.status === 'recognizing text') {
              this.setState({ ocrProgress: Math.round(m.progress * 100) });
            }
          }
        }
      );

      console.log("📝 OCR Raw Text:", text);

      // Extract VIN from the text
      // VIN is 17 characters: A-Z (except I, O, Q) and 0-9
      const vinPattern = /[A-HJ-NPR-Z0-9]{17}/g;
      const matches = text.toUpperCase().match(vinPattern);

      if (matches && matches.length > 0) {
        // Take the first match
        const detectedVIN = matches[0];
        console.log("✅ Detected VIN:", detectedVIN);

        this.setState({
          detectedVIN: detectedVIN,
          ocrProcessing: false,
          error: null
        });
      } else {
        console.log("⚠️ No VIN detected in image");
        this.setState({
          ocrProcessing: false,
          error: 'No VIN found in image. Please try again with a clearer photo.'
        });
      }
    } catch (error) {
      console.error("❌ OCR Error:", error);
      this.setState({
        ocrProcessing: false,
        error: 'Error processing image. Please try again.'
      });
    }
  };

  handleConfirm = () => {
    if (this.state.detectedVIN) {
      console.log("✅ User confirmed VIN:", this.state.detectedVIN);
      this.props.onVINScanned(this.state.detectedVIN);
    }
  };

  handleRetake = () => {
    this.setState({
      showCamera: true,
      photo: null,
      detectedVIN: null,
      error: null,
      ocrProgress: 0
    });
  };

  handleClose = () => {
    if (this.scanInterval) {
      clearInterval(this.scanInterval);
    }
    this.props.onClose();
  };

  // Real-time OCR scanning
  startRealtimeScanning = (videoElement) => {
    if (this.scanInterval) {
      clearInterval(this.scanInterval);
    }

    this.setState({ scanning: true });

    // Scan every 2 seconds
    this.scanInterval = setInterval(async () => {
      if (!videoElement || !this.canvasRef.current) return;

      try {
        const canvas = this.canvasRef.current;
        const video = videoElement;

        // Only scan if video is ready
        if (video.videoWidth === 0 || video.videoHeight === 0) return;

        // Set canvas size to video size (smaller for faster processing)
        canvas.width = Math.min(video.videoWidth, 640);
        canvas.height = Math.min(video.videoHeight, 480);

        // Draw current video frame to canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Get image data URL
        const imageData = canvas.toDataURL('image/jpeg', 0.6);

        // Run OCR on the frame
        const { data: { text } } = await Tesseract.recognize(
          imageData,
          'eng',
          {
            logger: () => {} // Silent
          }
        );

        // Look for VIN pattern
        const vinPattern = /[A-HJ-NPR-Z0-9]{17}/g;
        const matches = text.toUpperCase().match(vinPattern);

        if (matches && matches.length > 0) {
          const detectedVIN = matches[0];
          console.log("✅ Real-time VIN detected:", detectedVIN);

          this.setState({ realtimeVIN: detectedVIN });

          // Auto-confirm after 1 second
          setTimeout(() => {
            if (this.state.realtimeVIN === detectedVIN) {
              this.handleAutoCapture(detectedVIN);
            }
          }, 1000);
        }
      } catch (error) {
        console.error("OCR scan error:", error);
      }
    }, 2000); // Scan every 2 seconds
  };

  handleAutoCapture = (vin) => {
    console.log("🎯 Auto-capturing VIN:", vin);

    // Stop scanning
    if (this.scanInterval) {
      clearInterval(this.scanInterval);
    }

    this.setState({
      detectedVIN: vin,
      showCamera: false,
      scanning: false
    });
  };

  renderCamera = () => {
    if (this.state.error) {
      return (
        <div style={styles.container}>
          <div style={styles.errorContainer}>
            <div className="alert alert-danger">
              <h4>Camera Error</h4>
              <p>{this.state.error}</p>
              <button className="btn btn-primary" onClick={this.handleClose}>
                CLOSE
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={styles.container}>
        {/* Video element */}
        <video
          ref={this.videoRef}
          autoPlay
          playsInline
          style={styles.video}
          onLoadedMetadata={() => {
            console.log("📹 Video loaded, starting scanning");
            if (this.videoRef.current && !this.scanInterval) {
              this.startRealtimeScanning(this.videoRef.current);
            }
          }}
        />

        {/* Hidden canvas for OCR */}
        <canvas ref={this.canvasRef} style={{ display: 'none' }} />

        {/* Instructions Overlay */}
        <div style={styles.instructionsOverlay}>
          <div style={styles.instructionsBox}>
            <strong>📸 Point camera at VIN</strong>
            <p style={styles.instructionsText}>
              {this.state.scanning ? 'Scanning...' : 'Initializing camera...'}
            </p>
          </div>
        </div>

        {/* VIN Detection Overlay */}
        {this.state.realtimeVIN && (
          <div style={styles.detectionOverlay}>
            <div style={styles.detectionBox}>
              <div style={styles.detectionIcon}>✓</div>
              <div style={styles.detectionVIN}>{this.state.realtimeVIN}</div>
              <div style={styles.detectionText}>VIN Detected!</div>
            </div>
          </div>
        )}

        {/* Scanning indicator */}
        {this.state.scanning && !this.state.realtimeVIN && (
          <div style={styles.scanningIndicator}>
            <div style={styles.scanningBox}>
              <i className="fa fa-search"></i> Looking for VIN...
            </div>
          </div>
        )}

        {/* Cancel button */}
        <div style={styles.cameraControls}>
          <button
            className="btn btn-secondary btn-lg"
            onClick={this.handleClose}
          >
            <i className="fa fa-times mr-2"></i>
            CANCEL
          </button>
        </div>
      </div>
    );
  };

  render() {
    // Show camera
    if (this.state.showCamera) {
      return this.renderCamera();
    }

    // Show processing/results
    return (
      <div style={styles.container}>
        {/* Photo Preview */}
        {this.state.photo && (
          <div style={styles.photoPreview}>
            <img
              src={this.state.photo.preview}
              alt="VIN"
              style={styles.photoImage}
            />
          </div>
        )}

        {/* Processing Overlay */}
        {this.state.ocrProcessing && (
          <div style={styles.processingOverlay}>
            <div style={styles.processingBox}>
              <i className="fa fa-spinner fa-spin fa-3x text-primary mb-3"></i>
              <h5>Reading VIN from image...</h5>
              <div className="progress" style={{ width: '100%', marginTop: '15px' }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${this.state.ocrProgress}%` }}
                >
                  {this.state.ocrProgress}%
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {!this.state.ocrProcessing && (
          <div style={styles.resultsContainer}>
            {/* Success */}
            {this.state.detectedVIN && (
              <div>
                <div className="alert alert-success" role="alert">
                  <i className="fa fa-check-circle mr-2"></i>
                  VIN detected successfully!
                </div>

                <div style={styles.vinBox}>
                  <label style={styles.vinLabel}>Detected VIN:</label>
                  <div style={styles.vinText}>{this.state.detectedVIN}</div>
                </div>

                <div style={styles.buttonGroup}>
                  <button
                    className="btn btn-success btn-lg btn-block"
                    onClick={this.handleConfirm}
                  >
                    <i className="fa fa-check mr-2"></i>
                    Confirm VIN
                  </button>

                  <button
                    className="btn btn-outline-secondary btn-block"
                    onClick={this.handleRetake}
                  >
                    <i className="fa fa-camera mr-2"></i>
                    Retake Photo
                  </button>
                </div>
              </div>
            )}

            {/* Error */}
            {this.state.error && (
              <div>
                <div className="alert alert-danger" role="alert">
                  <i className="fa fa-exclamation-triangle mr-2"></i>
                  {this.state.error}
                </div>

                <div style={styles.helpBox}>
                  <p><strong>Tips for better results:</strong></p>
                  <ul>
                    <li>Ensure good lighting</li>
                    <li>Keep camera steady</li>
                    <li>Make sure VIN is in focus</li>
                    <li>Avoid glare and shadows</li>
                    <li>Get close enough to read the VIN clearly</li>
                  </ul>
                </div>

                <div style={styles.buttonGroup}>
                  <button
                    className="btn btn-primary btn-lg btn-block"
                    onClick={this.handleRetake}
                  >
                    <i className="fa fa-camera mr-2"></i>
                    Try Again
                  </button>

                  <button
                    className="btn btn-outline-secondary btn-block"
                    onClick={this.handleClose}
                  >
                    <i className="fa fa-times mr-2"></i>
                    CANCEL
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

// Styles
const styles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column'
  },
  instructionsOverlay: {
    position: 'absolute',
    top: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10000,
    pointerEvents: 'none',
    maxWidth: '90%',
    width: '400px'
  },
  instructionsBox: {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    padding: '15px 20px',
    borderRadius: '8px',
    border: '2px solid #007bff',
    color: '#fff',
    textAlign: 'left'
  },
  instructionsText: {
    fontSize: '14px',
    marginBottom: '5px',
    marginTop: '10px'
  },
  instructionsList: {
    fontSize: '13px',
    marginBottom: 0,
    paddingLeft: '20px'
  },
  photoPreview: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: '#000'
  },
  photoImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain'
  },
  processingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10
  },
  processingBox: {
    backgroundColor: '#fff',
    padding: '30px',
    borderRadius: '8px',
    textAlign: 'center',
    maxWidth: '400px',
    width: '90%'
  },
  resultsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    padding: '20px',
    maxHeight: '80%',
    overflowY: 'auto'
  },
  vinBox: {
    backgroundColor: '#f8f9fa',
    padding: '20px',
    borderRadius: '8px',
    border: '2px solid #28a745',
    marginBottom: '20px'
  },
  vinLabel: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#666',
    marginBottom: '10px',
    display: 'block'
  },
  vinText: {
    fontSize: '24px',
    fontFamily: 'monospace',
    fontWeight: 'bold',
    color: '#000',
    letterSpacing: '3px',
    textAlign: 'center'
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  helpBox: {
    backgroundColor: '#f8f9fa',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px'
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  detectionOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 10001
  },
  detectionBox: {
    backgroundColor: 'rgba(40, 167, 69, 0.95)',
    padding: '30px 40px',
    borderRadius: '12px',
    border: '3px solid #fff',
    textAlign: 'center',
    boxShadow: '0 0 30px rgba(40, 167, 69, 0.8)'
  },
  detectionIcon: {
    fontSize: '48px',
    color: '#fff',
    marginBottom: '10px',
    fontWeight: 'bold'
  },
  detectionVIN: {
    fontSize: '28px',
    fontFamily: 'monospace',
    fontWeight: 'bold',
    color: '#fff',
    letterSpacing: '4px',
    marginBottom: '10px'
  },
  detectionText: {
    fontSize: '18px',
    color: '#fff',
    fontWeight: 'bold'
  },
  scanningIndicator: {
    position: 'absolute',
    bottom: '100px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10000
  },
  scanningBox: {
    backgroundColor: 'rgba(0, 123, 255, 0.9)',
    padding: '12px 24px',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 'bold',
    border: '2px solid #fff'
  },
  cameraControls: {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10000
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    padding: '20px'
  }
};

export default VINScanner;
